import React, { useMemo, useState } from 'react';
import { Sheet, Btn, BasisChip, Chevron, money } from './ui';

const FEE = 0.07;
const MIN_MARGIN = 10000;

function Field({ label, children, note }) {
  return (
    <label className="block">
      <span className="block font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#6B7280', marginBottom: 6 }}>{label}</span>
      {children}
      {note && <span className="block uppercase" style={{ fontSize: 10, color: '#9CA3AF', marginTop: 6 }}>{note}</span>}
    </label>
  );
}

const toDollars = (cents) => (Math.round(cents || 0) / 100).toFixed(2);
const toCents = (s) => Math.round(parseFloat(String(s).replace(/[^0-9.]/g, '') || '0') * 100);

/**
 * EDIT CONCEPT — title, date/time (region-local), ticket, min guests, capacity, venue, cost
 * estimates. Recomputes goal + margin on every change; SAVE disables below the $100 minimum.
 */
export default function EditConceptModal({ concept, venues = [], timeZone, onClose, onSave, busy = false, serverError = null }) {
  const [title, setTitle] = useState(concept.title || '');
  const [date, setDate] = useState((concept.startsAtLocal || '').slice(0, 10));
  const [time, setTime] = useState((concept.startsAtLocal || '').slice(11, 16));
  const [ticket, setTicket] = useState(toDollars(concept.ticketPriceCents));
  const [minGuests, setMinGuests] = useState(concept.minGuests || 0);
  const [capacity, setCapacity] = useState(concept.maxAttendees || 0);
  const [venueId, setVenueId] = useState(concept.venueId || '');
  const [venueOpen, setVenueOpen] = useState(false);
  const [rental, setRental] = useState(toDollars(concept.venueCostEstimateCents || 0));
  const [perHead, setPerHead] = useState(toDollars(concept.perHeadCostCents || 0));

  const venue = venues.find((v) => v.id === venueId);
  // Every option is a verified venue. The chip is about where the NUMBER came from.
  const basis = !venue
    ? concept.venueCostBasis
    : venue.source === 'pro_listing' && venue.listedPriceCents > 0 ? 'pro_listing'
      : venue.listedPriceCents > 0 ? 'web_verified' : 'llm_estimate';
  const rentalLocked = basis === 'pro_listing' || basis === 'web_verified';
  const rentalCents = rentalLocked && venue ? venue.listedPriceCents : toCents(rental);

  const econ = useMemo(() => {
    const t = toCents(ticket);
    const g = Math.max(0, parseInt(minGuests, 10) || 0);
    const goal = t * g;
    const costs = rentalCents + toCents(perHead) * g;
    const margin = Math.round(goal - goal * FEE - costs - costs * 0.10);
    return { goal, margin, ticket: t, guests: g };
  }, [ticket, minGuests, rentalCents, perHead]);

  const invalidMargin = econ.margin < MIN_MARGIN;
  const capacityBad = (parseInt(capacity, 10) || 0) < econ.guests;
  const canSave = !busy && !invalidMargin && !capacityBad && title.trim().length >= 8 && date && time && !!venue;

  const submit = () => {
    onSave({
      title: title.trim(),
      startsAtLocal: `${date}T${time}`,
      ticketPriceCents: econ.ticket,
      minGuests: econ.guests,
      maxAttendees: parseInt(capacity, 10) || econ.guests,
      venueId: venueId || undefined,
      venueCostEstimateCents: rentalLocked ? undefined : rentalCents,
      perHeadCostCents: toCents(perHead),
    });
  };

  const venueLabel = venue?.name || concept.venueName || 'Pick a verified venue';

  return (
    <Sheet
      width={560}
      title="EDIT CONCEPT"
      onClose={onClose}
      footer={(
        <>
          <Btn variant="secondary" onClick={onClose} disabled={busy}>CANCEL</Btn>
          <Btn variant="primary" onClick={submit} disabled={!canSave}>{busy ? 'SAVING…' : 'SAVE'}</Btn>
        </>
      )}
    >
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="TITLE">
          <input className="pe-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
        </Field>
        <div>
          <div className="grid grid-cols-2" style={{ gap: 12 }}>
            <Field label="DATE"><input className="pe-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
            <Field label="TIME"><input className="pe-input" type="time" step={1800} value={time} onChange={(e) => setTime(e.target.value)} /></Field>
          </div>
          <span className="block uppercase" style={{ fontSize: 10, color: '#9CA3AF', marginTop: 6 }}>LOCAL TO REGION — {(timeZone || 'REGION TIME').toUpperCase()}</span>
        </div>
        <div className="grid grid-cols-3" style={{ gap: 12 }}>
          <Field label="TICKET"><input className={`pe-input ${invalidMargin ? 'pe-invalid' : ''}`} inputMode="decimal" value={ticket} onChange={(e) => setTicket(e.target.value)} /></Field>
          <Field label="MIN GUESTS"><input className={`pe-input ${invalidMargin ? 'pe-invalid' : ''}`} type="number" min={1} value={minGuests} onChange={(e) => setMinGuests(e.target.value)} /></Field>
          <Field label="CAPACITY"><input className={`pe-input ${capacityBad ? 'pe-invalid' : ''}`} type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} /></Field>
        </div>
        <div>
          <span className="block font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#6B7280', marginBottom: 6 }}>VENUE</span>
          <div className="relative">
            <button type="button" onClick={() => setVenueOpen((o) => !o)} className="pe-input flex items-center justify-between gap-2 text-left">
              <span className="flex items-center gap-2 min-w-0">
                <span className="truncate">{venueLabel}</span>
                <BasisChip basis={basis} href={venue?.evidenceUrl || concept.venueEvidenceUrl} />
              </span>
              <Chevron open={venueOpen} />
            </button>
            {venueOpen && (
              <div className="absolute left-0 right-0 bg-white z-10" style={{ border: '2px solid #000', borderTop: 'none', boxShadow: '4px 4px 0 0 #000', maxHeight: 260, overflowY: 'auto' }}>
                {venues.map((v, i) => (
                  <button
                    type="button"
                    key={v.id}
                    onClick={() => { setVenueId(v.id); setVenueOpen(false); }}
                    className="w-full flex items-center justify-between gap-2 text-left hover:bg-[#F5F5F5]"
                    style={{ padding: '8px 11px', borderTop: i === 0 ? 'none' : '2px solid #000', background: v.id === venueId ? '#F5F5F5' : '#fff', fontSize: 13 }}
                  >
                    <span className="truncate">{v.name} <span style={{ color: '#9CA3AF' }}>· {v.kind}</span></span>
                    <span className="font-bold uppercase whitespace-nowrap" style={{ fontSize: 10, color: '#6B7280' }}>
                      {v.source === 'pro_listing' ? `PRO${v.listedPriceCents ? ` · ${money(v.listedPriceCents)}` : ''}` : v.listedPriceCents ? `VERIFIED · ${money(v.listedPriceCents)}` : 'VERIFIED · RATE ESTIMATED'}
                    </span>
                  </button>
                ))}
                {venues.length === 0 && (
                  <div className="uppercase" style={{ padding: '8px 11px', fontSize: 11, color: '#6B7280' }}>NO VERIFIED VENUES IN THIS RUN.</div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2" style={{ gap: 12 }}>
          <Field label="VENUE RENTAL ESTIMATE" note={rentalLocked ? (basis === 'pro_listing' ? 'FROM THE PRO LISTING' : 'POSTED ON THE VENUE\'S PAGE') : undefined}>
            <input className="pe-input" inputMode="decimal" value={rentalLocked ? toDollars(rentalCents) : rental} disabled={rentalLocked} onChange={(e) => setRental(e.target.value)} />
          </Field>
          <Field label="PER-HEAD COST">
            <input className="pe-input" inputMode="decimal" value={perHead} onChange={(e) => setPerHead(e.target.value)} />
          </Field>
        </div>
      </div>
      <div className="border-t border-[#F2F2F2]" style={{ padding: '11px 16px' }}>
        <div className="font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.05em', color: invalidMargin ? '#DC2626' : '#000' }}>
          GOAL {money(econ.goal)} · HOST MARGIN AT GOAL {money(econ.margin)}
        </div>
        <div className="uppercase" style={{ fontSize: 10, color: '#6B7280', marginTop: 2 }}>AFTER 7% FEE</div>
        {invalidMargin && <div className="font-bold uppercase" style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>MARGIN BELOW {money(MIN_MARGIN)} MINIMUM</div>}
        {capacityBad && <div className="font-bold uppercase" style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>CAPACITY BELOW MIN GUESTS</div>}
        {serverError && <div className="uppercase" style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>{serverError}</div>}
      </div>
    </Sheet>
  );
}
